import { useCallback, useState } from "react";
import {
  CustomDialog,
  Header,
  Content,
  Footer,
  Text,
  Image,
  ButtonGroup,
  Button,
  CloseButton,
  Skeleton,
} from "@react-spectrum/s2";
import { ListBox, ListBoxItem } from "react-aria-components";
import { useAsyncList } from "react-stately";
import { getElements } from "@app/services/ccLibraries";
import { getVisualUrl } from "@app/utils";

import DropToUpload from "@react-spectrum/s2/illustrations/linear/DropToUpload";

import {
  style,
  lightDark,
} from "@react-spectrum/s2/style" with { type: "macro" };

export const ImageSelectionDialog = ({
  libraryId,
  images,
  onPrimaryAction,
}) => {
  const [imageReference, setImageReference] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(false);

  const listData = useAsyncList({
    getKey: (item) => item.id,
    async load({ cursor, signal }) {
      try {
        if (!libraryId && images) {
          // Fetch all image URLs in parallel
          const itemsWithUrls = await Promise.all(
            images.map(async (image) => {
              let url;
              try {
                url = await getVisualUrl({
                  id: image.avatarId,
                  libraryId: image.libraryId,
                  url: image.url, // Use existing URL if available
                });
              } catch (error) {
                console.warn(`Failed to get image URL for ${image.id}:`, error);
                url =
                  "https://react-spectrum.adobe.com/s2/preview.c3b340d3.png";
              }
              return {
                name: image.id,
                libraryId: image.libraryId,
                id: image.avatarId,
                url,
              };
            }),
          );

          return {
            items: itemsWithUrls,
            selectedKeys: new Set(imageReference ? [imageReference?.id] : []),
          };
        }

        // Parse cursor to get start, default to 0 for initial load
        const start = cursor ? parseInt(cursor, 10) : 0;
        const limit = 10; // Smaller page size for better UX

        // { description, id, name, tags }
        const data = await getElements({
          libraryId,
          limit,
          start,
        });

        // Check if request was aborted
        if (signal?.aborted) {
          throw new Error("Request was aborted");
        }

        // Fetch all element URLs in parallel
        const itemsWithUrls = await Promise.all(
          (data?.elements || []).map(async (element) => {
            let url;
            try {
              url = await getVisualUrl({
                id: element?.thumbnail?.componentId,
                libraryId: libraryId,
                url: element?.thumbnail?.url, // Use existing URL if available
              });
            } catch (error) {
              console.warn(
                `Failed to get image URL for ${element.name}:`,
                error,
              );
              url = "https://react-spectrum.adobe.com/s2/preview.c3b340d3.png";
            }
            return {
              name: element.name,
              description: element?.details?.description,
              libraryId: libraryId,
              id: element?.thumbnail?.componentId,
              url,
            };
          }),
        );

        // Calculate next cursor (offset for next page)
        const nextOffset = start + limit;
        const hasMoreData = data?._links?.next?.href;

        setHasMoreData(hasMoreData);

        return {
          items: itemsWithUrls,
          cursor: hasMoreData ? nextOffset.toString() : undefined,
          selectedKeys: new Set(imageReference ? [imageReference?.id] : []),
        };
      } catch (error) {
        console.error("Failed to load images:", error);
        if (error.message === "Request was aborted") {
          throw error;
        }
        return { items: [], selectedKeys: new Set() };
      }
    },
  });

  const handleSelectionChange = (selectedKeys) => {
    listData.setSelectedKeys(selectedKeys);

    setImageReference(listData.getItem(selectedKeys.currentKey) ?? null);
  };

  const handleSave = useCallback(() => {
    onPrimaryAction(imageReference);
  }, [imageReference]);

  const renderLoadMoreSkeleton = () => {
    if (listData.isLoading) {
      return (
        <>
          {new Array(9).fill(null).map((_, index) => (
            <Skeleton key={`loadmore-${index}`} isLoading>
              <div
                alt="Loading more images"
                className={style({
                  borderRadius: "lg",
                  overflow: "hidden",
                  outlineColor: {
                    default: "transparent",
                  },
                  outlineOffset: -2,
                  outlineStyle: "solid",
                  outlineWidth: 2,
                })}
              >
                <Image
                  styles={style({
                    size: "full",
                    aspectRatio: "4/3",
                    objectFit: "cover",
                  })}
                />
              </div>
            </Skeleton>
          ))}
        </>
      );
    }
    return null;
  };

  return (
    <CustomDialog
      isDismissable
      styles={style({
        maxWidth: "90vw",
        width: {
          default: "full",
          sm: 1200,
        },
      })}
    >
      {({ close }) => (
        <div
          className={style({
            size: "full",
            display: "grid",
            gridTemplateAreas: [
              "header header header",
              ". . .",
              "content content content",
              ". . .",
              "footer footer buttonGroup",
            ],
            gridTemplateRows: ["auto", 16, "1fr", 32, "auto"],
          })}
        >
          <Header
            styles={style({
              gridArea: "header",
              font: "title-2xl",
              marginTop: 0,
            })}
          >
            <Text>Select image</Text>
          </Header>
          <Content styles={style({ gridArea: "content" })}>
            <div
              className={style({
                size: "full",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              })}
            >
              <ListBox
                aria-label="Select generated images visual style"
                items={listData.items}
                selectionMode="single"
                selectedKeys={listData.selectedKeys}
                onSelectionChange={handleSelectionChange}
                layout="grid"
                className={style({
                  size: "full",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 16,
                  width: "[fit-content]",
                  maxWidth: "100%",
                })}
              >
                {(item) => (
                  <ListBoxItem
                    id={item.id}
                    textValue={item.name}
                    className={style({
                      borderRadius: "lg",
                      overflow: "hidden",
                      outlineColor: {
                        default: "transparent",
                        isHovered: lightDark(
                          "transparent-black-400",
                          "transparent-white-400",
                        ),
                        isFocused: lightDark(
                          "transparent-black-600",
                          "transparent-white-600",
                        ),
                        isSelected: "gray-900",
                      },
                      outlineOffset: -2,
                      outlineStyle: "solid",
                      outlineWidth: 2,
                      transition: "transform",
                      willChange: "transform",
                      transform: {
                        default: "none",
                        isPressed: "perspective(160px) translate3d(0, 0, -2px)",
                      },
                    })}
                  >
                    {item.name !== "NEW" ? (
                      <Image
                        src={item.url}
                        alt={item.name}
                        styles={style({
                          size: "full",
                          aspectRatio: "4/3",
                          objectFit: "cover",
                        })}
                      />
                    ) : (
                      <div
                        className={style({
                          size: "full",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 12,
                        })}
                      >
                        <DropToUpload styles={style({ size: 48 })} />
                        <Text
                          styles={style({ font: "ui-sm", textAlign: "center" })}
                        >
                          Drag and drop new image
                        </Text>
                      </div>
                    )}
                  </ListBoxItem>
                )}
              </ListBox>

              {/* Loading skeleton for more items */}
              {listData.isLoading && (
                <div
                  className={style({
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 16,
                    width: "[fit-content]",
                    maxWidth: "100%",
                  })}
                >
                  {renderLoadMoreSkeleton()}
                </div>
              )}

              {/* Load More Button */}
              {hasMoreData && !listData.isLoading && (
                <div
                  className={style({
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: 8,
                  })}
                >
                  <Button
                    variant="secondary"
                    onPress={() => {
                      listData.loadMore();
                    }}
                    isDisabled={listData.isLoading}
                  >
                    <Text>Load More Images</Text>
                  </Button>
                </div>
              )}
            </div>
          </Content>
          <Footer styles={style({ gridArea: "footer" })}></Footer>
          <ButtonGroup
            styles={style({
              gridArea: "buttonGroup",
              justifySelf: "end",
            })}
          >
            <Button
              variant="genai"
              isDisabled={listData.isLoading}
              onPress={() => {
                handleSave();
                close();
              }}
            >
              <Text>Save</Text>
            </Button>
          </ButtonGroup>
          <CloseButton
            styles={style({
              position: "absolute",
              top: 12,
              insetEnd: 12,
            })}
          />
        </div>
      )}
    </CustomDialog>
  );
};
